import React, { useEffect, useState } from 'react';
import { Field, Form, Formik, useFormikContext } from 'formik';
import { initCaseFormChange, setFormChanged } from 'app/shared/reducers/case-form-change';
import { Col, FormGroup, Row } from 'reactstrap';
import { Button, Input, Label } from 'edozvola-frontend-lib/dist/ui';
import { FormikPredictiveInput } from 'edozvola-frontend-lib/dist/ui/components/predictive-input/FormikPredictiveInput';
import { useParams } from 'react-router-dom';
import { PlannedDocumentation } from 'app/edozvola/cases/cases.model';
import { CodebookModel } from 'app/shared/model/shared.model';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { ERRORS, messages } from 'app/config/constants';
import { fetchProjectEngineerStatementCodebook, fetchUrbanPlanObligation } from 'app/edozvola/codebooks/codebooks.service';
import { fetchPlannedDocumentation, savePlannedDocumentation } from 'app/edozvola/cases/service/cases.service';
import { showToast } from 'app/shared/util/toast.util';
import { toast } from 'react-toastify';
import {updateCasePlannedDocumentationMutation} from "app/edozvola/cases/service/cases.queries";

const PlannedDocumentationForm = () => {
  const { id } = useParams<{ id: string }>();
  const [initialData, setInitialData] = useState<PlannedDocumentation>();
  const [urbanPlanObligationCodebookOptions, setUrbanPlanObligationCodebookOptions] = useState<CodebookModel[]>([]);
  const [projectEngineerStatementCodebookOptions, setProjectEngineerStatementCodebookOptions] = useState<CodebookModel[]>([]);
  const dispatch = useDispatch();
  const {mutate: saveMutation, isPending: isSaving} = updateCasePlannedDocumentationMutation(id, data => {
    setInitialData(data);
    showToast(toast.success, messages.DATA_SAVED);
  }, error => {
    if (Object.values(ERRORS).includes(error.data.message)) {
      showToast(toast.error, error.data.message);
    } else {
      showToast(toast.error, ERRORS.GENERIC_ERROR);
    }
  });

  useEffect(() => {
    fetchCodebooks();
    fetchPlannedDocumentation(id).then(data => {
      setInitialData(data);
    });
  }, [id]);

  const fetchCodebooks = () => {
    fetchUrbanPlanObligation().then(data => setUrbanPlanObligationCodebookOptions(data));
    fetchProjectEngineerStatementCodebook().then(data => setProjectEngineerStatementCodebookOptions(data));
  };

  const validation = Yup.object({
    compatibilityExplanation: Yup.string().required(messages.MANDATORY_FIELD),
  });

  const FormikValueObserver = ({ onChange }) => {
    const { dirty } = useFormikContext();

    useEffect(() => onChange({ dirty }), [dirty, onChange]);

    return null;
  };

  const handleSubmit = (values, { setSubmitting }) => {
    saveMutation(values);
  };

  return (
    <>
      <Formik enableReinitialize={true} initialValues={initialData} validationSchema={validation} onSubmit={handleSubmit}>
        {({ errors, isValid, dirty, submitForm, setFieldValue }) => (
          <Form>
            <FormikValueObserver
              onChange={dirty => {
                if (dirty.dirty) {
                  dispatch(setFormChanged(true));
                } else {
                  dispatch(initCaseFormChange());
                }
              }}
            />
            <Row>
              <Col xs={12}>
                <FormGroup>
                  <Label labelText={'Prostorni plan - obrazloženje usklađenosti'} />
                  <Field
                    id="compatibilityExplanation"
                    name="compatibilityExplanation"
                    render={({ field }) => <Input {...field} type="textarea" error={errors.compatibilityExplanation as string} />}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <FormGroup>
                  <Label labelText={'Urbanistički plan - obveza donošenja'} />
                  <Field
                    id="upEnactmentObligation"
                    name="upEnactmentObligation"
                    render={({ field }) => (
                      <FormikPredictiveInput options={urbanPlanObligationCodebookOptions} field={field} setFieldValue={setFieldValue} />
                    )}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <FormGroup>
                  <Label labelText={'Izjave projektanata'} />
                  <Field
                    id="projectEngineerStatement"
                    name="projectEngineerStatement"
                    render={({ field }) => (
                      <FormikPredictiveInput
                        options={projectEngineerStatementCodebookOptions}
                        field={field}
                        setFieldValue={setFieldValue}
                      />
                    )}
                  />
                </FormGroup>
              </Col>
            </Row>

            <div className={'case-save-button'}>
              <Button color={'info'} label={'Spremi'} size={'lg'} isLoading={isSaving} onClick={submitForm} disabled={!dirty || !isValid} />
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default PlannedDocumentationForm;
